import {
  deleteMessageFromQueue,
  pushMessageToQueue,
  resetScheduleMessageState,
  selectMessageScheduleState,
  setScheduleDateTime,
  setMsgListQueue,
  setScheduleMessageType,
  setShowScheduleOptions,
} from "../../Slices/MessageDialogScheduleSlice";
import { useAppDispatch, useAppSelector } from "../reduxHooks";
import { useMessageInputState } from "./useMessageInput";
import dayjs, { Dayjs } from "dayjs";
import { IMessageScheduleQueue, MessageType } from "../../../Typings/MessageText";
import { useCallback } from "react";
import { getDateUtc, getNowUtc, toNearestFiveMinutes } from "../../../../MessagingCenter/Utils";

export function useMessageScheduleState() {
  return useAppSelector(selectMessageScheduleState);
}

export function useMessageScheduleHandlers() {
  const dispatch = useAppDispatch();
  const { body, subject, bodyErrorMessage, subjectErrorMessage, textSplitCount } = useMessageInputState();
  const { scheduleDateTime, scheduleDateTimeErrorMessage, showScheduleOptions } = useMessageScheduleState();

  const handleHideScheduleOptions = () => {
    dispatch(setShowScheduleOptions(false));
  };
  const handleShowScheduleOptions = () => {
    dispatch(setShowScheduleOptions(true));
  };

  const handleClearScheduleOptions = () => {
    dispatch(resetScheduleMessageState());
  };

  const handleClickScheduleButton = () => {
    if (showScheduleOptions) {
      handleHideScheduleOptions();
    } else {
      handleShowScheduleOptions();
    }
  };

  const handleCalendarChange = (newValue: Dayjs | null) => {
    const newDate = newValue?.second(0).millisecond(0);
    if (!newDate) {
      dispatch(setScheduleDateTime({ time: null }));
      return;
    }
    const roundedDate = toNearestFiveMinutes(newDate);
    dispatch(setScheduleDateTime({ time: roundedDate.toString() }));
  };

  const handleAddMessageToListQueue = useCallback(
    () => {
      if (!!bodyErrorMessage || !!subjectErrorMessage || !!scheduleDateTimeErrorMessage) {
        return;
      }
      const scheduledTime = getDateUtc(scheduleDateTime ?? "");
      const isCalendarDateValid = scheduledTime.isValid();
      const roundedTime = isCalendarDateValid ? toNearestFiveMinutes(scheduledTime) : toNearestFiveMinutes();
      const newMsg: IMessageScheduleQueue = {
        id: getNowUtc().toISOString(),
        date: roundedTime,
        title: subject || "",
        bodyRaw: body,
        scheduled: true,
        segments: textSplitCount ?? 1,
      };
      dispatch(pushMessageToQueue(newMsg));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [body, subject, scheduleDateTime, bodyErrorMessage, subjectErrorMessage, scheduleDateTimeErrorMessage],
  );

  const handleDeleteMessageFromQueue = (msg: IMessageScheduleQueue) => {
    dispatch(deleteMessageFromQueue(msg));
  };
  const handleReplaceMessageQueue = (messages: IMessageScheduleQueue[]) => {
    dispatch(setMsgListQueue(messages));
  };

  const handleClearMessageQueue = () => {
    dispatch(setMsgListQueue([]));
  };

  const handleChangeMessageType = (messageType: MessageType) => {
    dispatch(setScheduleMessageType(messageType));
  };

  const handleValidateScheduleInput = useCallback(() => {
    const isCalendarDateValid = dayjs(scheduleDateTime).isValid();
    return isCalendarDateValid && !scheduleDateTimeErrorMessage;
  }, [scheduleDateTime, scheduleDateTimeErrorMessage]);

  return {
    handleHideScheduleOptions,
    handleClearScheduleOptions,
    handleClickScheduleButton,
    handleCalendarChange,
    handleShowScheduleOptions,
    handleAddMessageToListQueue,
    handleDeleteMessageFromQueue,
    handleReplaceMessageQueue,
    handleValidateScheduleInput,
    handleChangeMessageType,
    handleClearMessageQueue,
  } as const;
}
